import type {
  FormComponentType,
  FormField,
  FormRowField,
  GroupField,
} from './types'

export const DEFAULT_CHOICE_OPTIONS = ['Option 1', 'Option 2']
export const LEGACY_GENDER_OPTIONS = ['Male', 'Female', 'Other']

const CHOICE_FIELD_TYPES = new Set<FormComponentType>([
  'Checkbox Group',
  'Dropdown',
  'Radio Group',
])

export function defaultLabelForFieldType(type: FormComponentType): string {
  if (type === 'Signature Field') return 'Patient Signature'
  if (type === 'Dentist/Admin Signature') return 'Dentist/Admin Signature'
  return ''
}

export function createFormField(
  type: FormComponentType,
  id: string,
): FormField {
  const field: FormField = {
    id,
    type,
    label: defaultLabelForFieldType(type),
  }
  if (CHOICE_FIELD_TYPES.has(type)) {
    field.options = [...DEFAULT_CHOICE_OPTIONS]
  }
  return field
}

/** Options shown to the patient. Blank entries are omitted. */
export function choiceOptionsForField(
  field: Pick<FormField, 'type' | 'options'>,
): string[] {
  if (field.options != null && field.options.length > 0) {
    return field.options
      .map((option) => option.trim())
      .filter((option) => option.length > 0)
  }
  if (field.type === 'Gender Selection') return [...LEGACY_GENDER_OPTIONS]
  if (CHOICE_FIELD_TYPES.has(field.type)) return [...DEFAULT_CHOICE_OPTIONS]
  return []
}

/** Options in the field settings editor, including in-progress blank rows. */
export function editableOptionsForField(
  field: Pick<FormField, 'type' | 'options'>,
): string[] {
  if (field.options != null && field.options.length > 0) {
    return [...field.options]
  }
  return choiceOptionsForField(field)
}

function isFormRowNode(
  item: FormField | GroupField | FormRowField,
): item is FormRowField {
  return 'type' in item && item.type === 'Form Row' && 'fields' in item
}

function isGroupNode(
  item: FormField | GroupField | FormRowField,
): item is GroupField {
  return 'name' in item && 'fields' in item && !isFormRowNode(item)
}

function normalizeLeaf(field: FormField): FormField {
  if (field.type !== 'Gender Selection') return field
  return {
    ...field,
    type: 'Radio Group',
    options:
      field.options != null && field.options.length > 0
        ? field.options
        : [...LEGACY_GENDER_OPTIONS],
  }
}

function normalizeGroup(group: GroupField): GroupField {
  return {
    ...group,
    fields: group.fields.map((item) => {
      if (isFormRowNode(item)) {
        return {
          ...item,
          fields: item.fields.map((field) => normalizeLeaf(field)),
        }
      }
      if (isGroupNode(item)) return normalizeGroup(item)
      return normalizeLeaf(item as FormField)
    }),
  }
}

/** Turns saved Gender Selection fields into a radio group with the same choices. */
export function normalizeLoadedGroups(groups: GroupField[]): GroupField[] {
  return groups.map((group) => normalizeGroup(group))
}
